/**
 * SEO 工具函数 - 动态更新页面 meta 标签
 */

const SITE_NAME = '为知实验室'
const SITE_URL = 'https://vizlab.cn'
const DEFAULT_DESCRIPTION = '交互式科学概念可视化平台，通过参数调节和实时可视化帮助用户理解基础科学原理。'

/**
 * 更新页面标题
 * @param {string} title - 页面标题
 */
export function updateTitle(title) {
  if (title) {
    document.title = `${title} - ${SITE_NAME}`
  } else {
    document.title = SITE_NAME
  }
}

/**
 * 更新或创建 meta 标签
 * @param {string} name - meta 标签的 name 或 property
 * @param {string} content - meta 标签的内容
 * @param {boolean} isProperty - 是否为 property 属性（用于 Open Graph）
 */
export function updateMetaTag(name, content, isProperty = false) {
  if (!content) return

  const attr = isProperty ? 'property' : 'name'
  let meta = document.querySelector(`meta[${attr}="${name}"]`)

  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attr, name)
    document.head.appendChild(meta)
  }

  meta.setAttribute('content', content)
}

/**
 * 更新页面 SEO 信息
 * @param {Object} seoData - SEO 数据对象
 * @param {string} seoData.title - 页面标题
 * @param {string} seoData.description - 页面描述
 * @param {string} seoData.keywords - 关键词（可选）
 * @param {string} seoData.image - 预览图 URL（可选）
 * @param {string} seoData.url - 页面 URL（可选）
 */
export function updateSEO(seoData = {}) {
  const {
    title,
    description = DEFAULT_DESCRIPTION,
    keywords,
    image,
    url
  } = seoData

  // 更新标题
  if (title) {
    updateTitle(title)
  }

  // 更新基础 meta 标签
  updateMetaTag('description', description)
  if (keywords) {
    updateMetaTag('keywords', keywords)
  }

  // 更新 Open Graph 标签
  const ogUrl = url || window.location.href
  updateMetaTag('og:title', title ? `${title} - ${SITE_NAME}` : SITE_NAME, true)
  updateMetaTag('og:description', description, true)
  updateMetaTag('og:url', ogUrl, true)
  if (image) {
    updateMetaTag('og:image', image.startsWith('http') ? image : `${SITE_URL}${image}`, true)
  }

  // 更新 Twitter Card
  updateMetaTag('twitter:title', title ? `${title} - ${SITE_NAME}` : SITE_NAME)
  updateMetaTag('twitter:description', description)
  if (image) {
    updateMetaTag('twitter:image', image.startsWith('http') ? image : `${SITE_URL}${image}`)
  }

  // 更新 canonical URL
  updateCanonicalUrl(ogUrl)
}

/**
 * 更新 canonical URL
 * @param {string} url - 规范 URL
 */
export function updateCanonicalUrl(url) {
  let canonical = document.querySelector('link[rel="canonical"]')
  
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }

  canonical.setAttribute('href', url)
}

/**
 * 添加结构化数据（JSON-LD）
 * @param {Object} data - 结构化数据对象
 */
export function addStructuredData(data) {
  // 移除旧的同类型结构化数据
  const existing = document.querySelector(`script[type="application/ld+json"][data-seo-id="${data['@type']}"]`)
  if (existing) {
    existing.remove()
  }

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.setAttribute('data-seo-id', data['@type'])
  script.textContent = JSON.stringify(data, null, 2)
  document.head.appendChild(script)
}

/**
 * 生成网站结构化数据
 */
export function generateWebsiteStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  }
}

/**
 * 生成 Toy 页面的结构化数据
 * @param {Object} toyConfig - Toy 配置对象
 * @param {string} toyId - Toy ID
 */
export function generateToyStructuredData(toyConfig, toyId) {
  const url = `${SITE_URL}/toy/${toyId}`
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: toyConfig.title || toyId,
    description: toyConfig.description || `${toyConfig.title}的交互式可视化演示`,
    url: url,
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'CNY'
    },
    aggregateRating: toyConfig.likes ? {
      '@type': 'AggregateRating',
      ratingValue: '4.5',
      ratingCount: toyConfig.likes.toString()
    } : undefined
  }
}

